import {
  type ImageMetadata,
  type MediaCardType,
} from '@wandb/weave/common/types/media';

import {type RunHistoryRow} from '../../../types/run';
import {mediaFilePath} from '../../../util/media';
import {normalizeMediaFilepath} from '../../../util/requests';
import {getImageInfo} from '../../ImageCard/utils';
import {
  getAudioMedia,
  getBokehMediaPath,
  getHTMLMedia,
  getImageMedia,
  getMoleculeMediaPath,
  getObject3DMediaPath,
  getPlotlyMediaPath,
  getTableMediaPath,
  getVideoMedia,
  type TileMediaSpec,
} from '../../MediaCard';
import {type FilePathSpec} from './types';

type GetFilePathFn = (p: GetFilePathParams) => string;

type GetFilePathParams = TileMediaSpec & {
  historyRow: RunHistoryRow;
};

const getFilePathFnByMediaType: Record<MediaCardType, GetFilePathFn> = {
  audio: getAudioFilePath,
  image: getImageFilePath,
  video: getVideoFilePath,
  table: getTableFilePath,
  plotly: getPlotlyFilePath,
  html: getHTMLFilePath,
  object3D: getObject3DFilePath,
  bokeh: getBokehFilePath,
  molecule: getMoleculeFilePath,
};

export function getMediaFilePath(p: GetFilePathParams): string {
  const filePath = getFilePathFnByMediaType[p.mediaType](p);
  return normalizeMediaFilepath(filePath);
}

/**
 * Path for `tileMediaSpec.mediaStep` from history — known before any file
 * query. Does not walk earlier steps if this one is missing.
 *
 * Same row filter as `availableFiles.ts` (media key present) so split-key
 * history still resolves. Empty string from `getMediaFilePath` is treated
 * as missing.
 */
export function getRequestedPathSpec(
  tileMediaSpec: TileMediaSpec
): FilePathSpec | null {
  const {run, mediaKey, mediaStep} = tileMediaSpec;
  const historyRow = (run.history ?? []).find(
    row => row._step === mediaStep && row[mediaKey] != null
  );
  if (historyRow == null) {
    return null;
  }
  const filePath = getMediaFilePath({...tileMediaSpec, historyRow});
  if (filePath === '') {
    return null;
  }
  return {historyRow, filePath};
}

export function getRequestedFilePath(
  tileMediaSpec: TileMediaSpec
): string | null {
  return getRequestedPathSpec(tileMediaSpec)?.filePath ?? null;
}

function getAudioFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getAudioMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'audio')
  );
}

function getImageFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
  runSignature,
}: GetFilePathParams) {
  const stepMedia = getImageMedia({historyRow, mediaKey}, mediaIndex);
  if (stepMedia != null && !stepMedia.path && stepMedia.artifact_path) {
    return stepMedia.artifact_path;
  }
  const {imgFile: filePath} = getImageInfo({
    imgMedia: stepMedia as ImageMetadata | null,
    step: historyRow._step,
    mediaIndex,
    mediaKey,
    runSignature,
  });
  return filePath;
}

function getVideoFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getVideoMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'videos')
  );
}

function getTableFilePath({historyRow, mediaKey}: GetFilePathParams) {
  return getTableMediaPath({historyRow, mediaKey}) ?? '';
}

function getPlotlyFilePath({historyRow, mediaKey}: GetFilePathParams) {
  return getPlotlyMediaPath({historyRow, mediaKey}) ?? '';
}

function getHTMLFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getHTMLMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'html')
  );
}

function getObject3DFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getObject3DMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}

function getBokehFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getBokehMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}

function getMoleculeFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getMoleculeMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}
